import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";

import { trpc } from "../../lib/trpc";
import { uploadFile } from "../../lib/uploadFile";
import type { CropData } from "../../lib/utils";
import { parseCropData } from "../../lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type QueueData = {
  id: string;
  name: string;
  description?: string | null;
  isEnabled: boolean;
  teamSize?: number | null;
  teamCount?: number | null;
  allowFullTeams?: boolean | null;
  eloMin?: number | null;
  eloMax?: number | null;
  maxPartySize?: number | null;
  partyEloDisparity?: number | null;
  acceptTimeout?: number | null;
  noShowTimeout?: number | null;
  hostSelection?: string | null;
  teamFormation?: string | null;
  bannerUrl?: string | null;
  bannerSourceUrl?: string | null;
  bannerCropData?: { x: number; y: number; width: number; height: number } | null;
  iconUrl?: string | null;
  iconSourceUrl?: string | null;
  iconCropData?: { x: number; y: number; width: number; height: number } | null;
  matchmakingConfig?: unknown;
  rankConfig?: unknown;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useQueueSettingsForm(queueId: string, clubId: string | undefined) {
  const utils = trpc.useUtils();

  // ---- Form state ----

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [teamSize, setTeamSize] = useState<string>("");
  const [teamCount, setTeamCount] = useState<string>("2");
  const [allowFullTeams, setAllowFullTeams] = useState(true);
  const [eloMin, setEloMin] = useState<string>("");
  const [eloMax, setEloMax] = useState<string>("");
  const [maxPartySize, setMaxPartySize] = useState<string>("");
  const [partyEloDisparity, setPartyEloDisparity] = useState<string>("");
  const [acceptTimeout, setAcceptTimeout] = useState<string>("");
  const [noShowTimeout, setNoShowTimeout] = useState<string>("");
  const [hostSelection, setHostSelection] = useState<string>("");
  const [teamFormation, setTeamFormation] = useState<string>("");
  const [bannerUrl, setBannerUrl] = useState("");
  const [iconUrl, setIconUrl] = useState("");
  const [bannerSourceUrl, setBannerSourceUrl] = useState("");
  const [iconSourceUrl, setIconSourceUrl] = useState("");
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [pendingBannerSourceFile, setPendingBannerSourceFile] = useState<File | null>(null);
  const [pendingIconSourceFile, setPendingIconSourceFile] = useState<File | null>(null);
  const [pendingBannerBlob, setPendingBannerBlob] = useState<Blob | null>(null);
  const [pendingIconBlob, setPendingIconBlob] = useState<Blob | null>(null);
  const [bannerCropData, setBannerCropData] = useState<CropData | null>(null);
  const [iconCropData, setIconCropData] = useState<CropData | null>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const iconInputRef = useRef<HTMLInputElement>(null);

  // Rank config
  const [rankThresholds, setRankThresholds] = useState<Record<string, string>>({});
  const [primeTopN, setPrimeTopN] = useState<string>("");
  const [championTopN, setChampionTopN] = useState<string>("");

  // Matchmaking config
  const [mmEloBaseRange, setMmEloBaseRange] = useState<string>("");
  const [mmEloExpansionStep, setMmEloExpansionStep] = useState<string>("");
  const [mmEloMaxDisparity, setMmEloMaxDisparity] = useState<string>("");
  const [mmEloExpansionInterval, setMmEloExpansionInterval] = useState<string>("");
  const [mmMinMatchQuality, setMmMinMatchQuality] = useState<string>("");
  const [mmScoringEloWeight, setMmScoringEloWeight] = useState<string>("");
  const [mmScoringWaitWeight, setMmScoringWaitWeight] = useState<string>("");
  const [mmQualityDivisor, setMmQualityDivisor] = useState<string>("");
  const [mmPartyHighestWeight, setMmPartyHighestWeight] = useState<string>("");
  const [mmPartyAverageWeight, setMmPartyAverageWeight] = useState<string>("");
  const [mmTsDefaultMu, setMmTsDefaultMu] = useState<string>("");
  const [mmTsDefaultSigma, setMmTsDefaultSigma] = useState<string>("");
  const [mmTsBeta, setMmTsBeta] = useState<string>("");
  const [mmTsTau, setMmTsTau] = useState<string>("");
  const [mmTsDrawProbability, setMmTsDrawProbability] = useState<string>("");
  const [mmTsDisplayEloMultiplier, setMmTsDisplayEloMultiplier] = useState<string>("");
  const [mmBansBaseDuration, setMmBansBaseDuration] = useState<string>("");
  const [mmBansEscalation, setMmBansEscalation] = useState<string>("");
  const [mmBansHistoryWindow, setMmBansHistoryWindow] = useState<string>("");
  const [mmBansMaxDuration, setMmBansMaxDuration] = useState<string>("");
  const [mmReportingTimeout, setMmReportingTimeout] = useState<string>("");

  // Composition config
  const [compositionSlots, setCompositionSlots] = useState<{ role: string; count: string }[]>([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ---- Mutations ----

  const updateMutation = trpc.queue.update.useMutation({
    onSuccess: () => {
      toast.success("Queue settings saved");
      void utils.queue.get.invalidate({ queueId });
      void utils.queue.list.invalidate();
    },
    onError: (err) => toast.error(err.message || "Failed to save"),
  });

  const deleteMutation = trpc.queue.delete.useMutation({
    onError: (err) => toast.error(err.message || "Failed to delete queue"),
  });

  const setEnabledMutation = trpc.queue.setEnabled.useMutation({
    onSuccess: () => {
      void utils.queue.get.invalidate({ queueId });
    },
  });

  // ---- File helpers ----

  const doUpload = async (
    file: File | Blob,
    category: "queue-icon" | "queue-icon-source" | "queue-banner" | "queue-banner-source",
  ): Promise<string> => {
    const { publicUrl } = await uploadFile(file, category, queueId);
    if (!publicUrl) throw new Error("Upload did not return a public URL");
    return publicUrl;
  };

  // ---- Populate form from queue data ----

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- stable setter references
  const populateForm = useCallback((queue: QueueData) => {
    setName(queue.name);
    setDescription(queue.description ?? "");
    setEnabled(queue.isEnabled);
    setTeamSize(queue.teamSize?.toString() ?? "");
    setTeamCount(queue.teamCount?.toString() ?? "2");
    setAllowFullTeams(queue.allowFullTeams ?? true);
    setEloMin(queue.eloMin?.toString() ?? "");
    setEloMax(queue.eloMax?.toString() ?? "");
    setMaxPartySize(queue.maxPartySize?.toString() ?? "");
    setPartyEloDisparity(queue.partyEloDisparity?.toString() ?? "");
    setAcceptTimeout(queue.acceptTimeout?.toString() ?? "");
    setNoShowTimeout(queue.noShowTimeout?.toString() ?? "");
    setHostSelection(queue.hostSelection ?? "");
    setTeamFormation(queue.teamFormation ?? "");
    setBannerUrl(queue.bannerUrl ?? "");
    setBannerSourceUrl(queue.bannerSourceUrl ?? "");
    setIconUrl(queue.iconUrl ?? "");
    setIconSourceUrl(queue.iconSourceUrl ?? "");

    // Rank config
    const rc = queue.rankConfig as {
      thresholds?: Record<string, number>;
      primeTopN?: number | null;
      championTopN?: number | null;
    } | null;
    if (rc?.thresholds) {
      setRankThresholds(
        Object.fromEntries(Object.entries(rc.thresholds).map(([k, v]) => [k, v.toString()])),
      );
    } else {
      setRankThresholds({});
    }
    setPrimeTopN(rc?.primeTopN?.toString() ?? "");
    setChampionTopN(rc?.championTopN?.toString() ?? "");

    // Matchmaking config
    const mc = queue.matchmakingConfig as Record<string, Record<string, number | undefined>> | null;
    if (mc) {
      setMmEloBaseRange(mc.eloWindow?.baseRange?.toString() ?? "");
      setMmEloExpansionStep(mc.eloWindow?.expansionStep?.toString() ?? "");
      setMmEloMaxDisparity(mc.eloWindow?.maxDisparity?.toString() ?? "");
      setMmEloExpansionInterval(mc.eloWindow?.expansionIntervalSeconds?.toString() ?? "");
      setMmMinMatchQuality(mc.quality?.minMatchQuality?.toString() ?? "");
      setMmScoringEloWeight(mc.quality?.scoringEloWeight?.toString() ?? "");
      setMmScoringWaitWeight(mc.quality?.scoringWaitWeight?.toString() ?? "");
      setMmQualityDivisor(mc.quality?.qualityDivisor?.toString() ?? "");
      setMmPartyHighestWeight(mc.party?.ratingHighestWeight?.toString() ?? "");
      setMmPartyAverageWeight(mc.party?.ratingAverageWeight?.toString() ?? "");
      setMmTsDefaultMu(mc.trueskill?.defaultMu?.toString() ?? "");
      setMmTsDefaultSigma(mc.trueskill?.defaultSigma?.toString() ?? "");
      setMmTsBeta(mc.trueskill?.beta?.toString() ?? "");
      setMmTsTau(mc.trueskill?.tau?.toString() ?? "");
      setMmTsDrawProbability(mc.trueskill?.drawProbability?.toString() ?? "");
      setMmTsDisplayEloMultiplier(mc.trueskill?.displayEloMultiplier?.toString() ?? "");
      setMmBansBaseDuration(mc.bans?.baseDurationSeconds?.toString() ?? "");
      setMmBansEscalation(mc.bans?.escalationMultiplier?.toString() ?? "");
      setMmBansHistoryWindow(mc.bans?.historyWindowHours?.toString() ?? "");
      setMmBansMaxDuration(mc.bans?.maxBanDurationSeconds?.toString() ?? "");
      setMmReportingTimeout(mc.reporting?.timeoutSeconds?.toString() ?? "");

      // Composition
      const comp = mc.composition as { slots?: { role: string; count: number }[] } | undefined;
      if (comp?.slots?.length) {
        setCompositionSlots(comp.slots.map((s) => ({ role: s.role, count: s.count.toString() })));
      } else {
        setCompositionSlots([]);
      }
    }

    // Restore crop data from server, clear pending files
    setBannerCropData(parseCropData(queue.bannerCropData));
    setIconCropData(parseCropData(queue.iconCropData));
    setPendingBannerSourceFile(null);
    setPendingIconSourceFile(null);
    setPendingBannerBlob(null);
    setPendingIconBlob(null);
  }, []);

  // ---- Computed ----

  const computedMatchSize = (Number(teamSize) || 0) * (Number(teamCount) || 0);
  const saving = isSaving || updateMutation.isPending || setEnabledMutation.isPending;

  // ---- Save handler ----

  const handleSave = async (currentQueue: QueueData) => {
    if (!clubId) return;
    setIsSaving(true);

    try {
      const optNum = (v: string) => (v ? Number(v) : undefined);
      const optNumNull = (v: string) => (v ? Number(v) : null);

      // Upload pending files (source + cropped)
      let newBannerUrl = bannerUrl || null;
      let newBannerSourceUrl = bannerSourceUrl || null;
      const newBannerCropData = bannerCropData;
      let newIconUrl = iconUrl || null;
      let newIconSourceUrl = iconSourceUrl || null;
      const newIconCropData = iconCropData;

      if (pendingBannerSourceFile) {
        newBannerSourceUrl = await doUpload(pendingBannerSourceFile, "queue-banner-source");
      }
      if (pendingBannerBlob) {
        newBannerUrl = await doUpload(pendingBannerBlob, "queue-banner");
      }
      if (pendingIconSourceFile) {
        newIconSourceUrl = await doUpload(pendingIconSourceFile, "queue-icon-source");
      }
      if (pendingIconBlob) {
        newIconUrl = await doUpload(pendingIconBlob, "queue-icon");
      }

      // Build matchmaking config
      const matchmakingConfig = {
        eloWindow: {
          baseRange: optNum(mmEloBaseRange),
          expansionStep: optNum(mmEloExpansionStep),
          maxDisparity: optNum(mmEloMaxDisparity),
          expansionIntervalSeconds: optNum(mmEloExpansionInterval),
        },
        quality: {
          minMatchQuality: optNum(mmMinMatchQuality),
          scoringEloWeight: optNum(mmScoringEloWeight),
          scoringWaitWeight: optNum(mmScoringWaitWeight),
          qualityDivisor: optNum(mmQualityDivisor),
        },
        party: {
          ratingHighestWeight: optNum(mmPartyHighestWeight),
          ratingAverageWeight: optNum(mmPartyAverageWeight),
        },
        trueskill: {
          defaultMu: optNum(mmTsDefaultMu),
          defaultSigma: optNum(mmTsDefaultSigma),
          beta: optNum(mmTsBeta),
          tau: optNum(mmTsTau),
          drawProbability: optNum(mmTsDrawProbability),
          displayEloMultiplier: optNum(mmTsDisplayEloMultiplier),
        },
        bans: {
          baseDurationSeconds: optNum(mmBansBaseDuration),
          escalationMultiplier: optNum(mmBansEscalation),
          historyWindowHours: optNum(mmBansHistoryWindow),
          maxBanDurationSeconds: optNum(mmBansMaxDuration),
        },
        reporting: {
          timeoutSeconds: optNum(mmReportingTimeout),
        },
        composition:
          compositionSlots.length > 0
            ? {
                slots: compositionSlots
                  .filter((s) => s.role.trim())
                  .map((s) => ({ role: s.role.trim(), count: Number(s.count) || 1 })),
              }
            : null,
      };

      // Build rank config
      const thresholds = Object.fromEntries(
        Object.entries(rankThresholds)
          .filter(([, v]) => v !== "")
          .map(([k, v]) => [k, Number(v)]),
      );

      await updateMutation.mutateAsync({
        queueId,
        clubId,
        name: name.trim(),
        description: description.trim() || undefined,
        teamSize: optNum(teamSize),
        matchSize: computedMatchSize > 0 ? computedMatchSize : undefined,
        teamCount: optNum(teamCount),
        allowFullTeams,
        eloMin: optNumNull(eloMin),
        eloMax: optNumNull(eloMax),
        maxPartySize: optNumNull(maxPartySize),
        partyEloDisparity: optNumNull(partyEloDisparity),
        acceptTimeout: optNumNull(acceptTimeout),
        noShowTimeout: optNumNull(noShowTimeout),
        hostSelection: (hostSelection || null) as "random" | "highest_elo" | "first_accept" | null,
        teamFormation: (teamFormation || null) as
          | "auto_balance"
          | "captains"
          | "random"
          | "self_select"
          | null,
        matchmakingConfig,
        rankConfig: {
          thresholds: Object.keys(thresholds).length > 0 ? thresholds : undefined,
          primeTopN: primeTopN ? Number(primeTopN) : null,
          championTopN: championTopN ? Number(championTopN) : null,
        },
        bannerUrl: newBannerUrl,
        bannerSourceUrl: newBannerSourceUrl,
        bannerCropData: newBannerCropData,
        iconUrl: newIconUrl,
        iconSourceUrl: newIconSourceUrl,
        iconCropData: newIconCropData,
      });

      setPendingBannerSourceFile(null);
      setPendingIconSourceFile(null);
      setPendingBannerBlob(null);
      setPendingIconBlob(null);

      // Also toggle enabled if changed
      if (enabled !== currentQueue.isEnabled) {
        await setEnabledMutation.mutateAsync({ queueId, clubId, enabled });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Delete handler ----

  const handleDelete = (_slug: string, onDeleted: () => void) => {
    if (!clubId) return;
    deleteMutation.mutate(
      { queueId, clubId },
      {
        onSuccess: () => {
          toast.success("Queue deleted");
          onDeleted();
        },
      },
    );
  };

  return {
    // Form fields — basic
    name,
    setName,
    description,
    setDescription,
    enabled,
    setEnabled,
    teamSize,
    setTeamSize,
    teamCount,
    setTeamCount,
    allowFullTeams,
    setAllowFullTeams,
    eloMin,
    setEloMin,
    eloMax,
    setEloMax,
    maxPartySize,
    setMaxPartySize,
    partyEloDisparity,
    setPartyEloDisparity,
    acceptTimeout,
    setAcceptTimeout,
    noShowTimeout,
    setNoShowTimeout,
    hostSelection,
    setHostSelection,
    teamFormation,
    setTeamFormation,

    // Images
    bannerUrl,
    setBannerUrl,
    bannerSourceUrl,
    iconUrl,
    setIconUrl,
    iconSourceUrl,
    bannerPreview,
    setBannerPreview,
    iconPreview,
    setIconPreview,
    bannerInputRef,
    iconInputRef,

    // Crop state
    pendingBannerSourceFile,
    setPendingBannerSourceFile,
    pendingIconSourceFile,
    setPendingIconSourceFile,
    pendingBannerBlob,
    setPendingBannerBlob,
    pendingIconBlob,
    setPendingIconBlob,
    bannerCropData,
    setBannerCropData,
    iconCropData,
    setIconCropData,

    // Rank config
    rankThresholds,
    setRankThresholds,
    primeTopN,
    setPrimeTopN,
    championTopN,
    setChampionTopN,

    // Matchmaking config
    mmEloBaseRange,
    setMmEloBaseRange,
    mmEloExpansionStep,
    setMmEloExpansionStep,
    mmEloMaxDisparity,
    setMmEloMaxDisparity,
    mmEloExpansionInterval,
    setMmEloExpansionInterval,
    mmMinMatchQuality,
    setMmMinMatchQuality,
    mmScoringEloWeight,
    setMmScoringEloWeight,
    mmScoringWaitWeight,
    setMmScoringWaitWeight,
    mmQualityDivisor,
    setMmQualityDivisor,
    mmPartyHighestWeight,
    setMmPartyHighestWeight,
    mmPartyAverageWeight,
    setMmPartyAverageWeight,
    mmTsDefaultMu,
    setMmTsDefaultMu,
    mmTsDefaultSigma,
    setMmTsDefaultSigma,
    mmTsBeta,
    setMmTsBeta,
    mmTsTau,
    setMmTsTau,
    mmTsDrawProbability,
    setMmTsDrawProbability,
    mmTsDisplayEloMultiplier,
    setMmTsDisplayEloMultiplier,
    mmBansBaseDuration,
    setMmBansBaseDuration,
    mmBansEscalation,
    setMmBansEscalation,
    mmBansHistoryWindow,
    setMmBansHistoryWindow,
    mmBansMaxDuration,
    setMmBansMaxDuration,
    mmReportingTimeout,
    setMmReportingTimeout,

    // Composition
    compositionSlots,
    setCompositionSlots,

    // UI state
    showDeleteModal,
    setShowDeleteModal,

    // Computed
    computedMatchSize,
    saving,

    // Actions
    populateForm,
    handleSave,
    handleDelete,
    deleteMutation,
  };
}

export type QueueSettingsForm = ReturnType<typeof useQueueSettingsForm>;
